import { useState, type FormEvent } from "react";

import { appPath } from "../appPath";
import {
  submitPersonalLawyerVerification,
  type PersonalLawyerProfile,
} from "../api/client";

const MATERIAL_LABELS = {
  IDENTITY_FRONT: "身份证人像面",
  IDENTITY_BACK: "身份证国徽面",
  PRACTICE_CERTIFICATE: "律师执业证",
};

function SubmittedVerification({ profile }: { profile: PersonalLawyerProfile }) {
  const approved = profile.status === "APPROVED";
  return (
    <section className="personal-lawyer-panel personal-lawyer-verification">
      <header className={`personal-lawyer-status ${approved ? "is-approved" : "is-pending"}`}>
        <span aria-hidden="true">{approved ? "✓" : "•"}</span>
        <div>
          <h1>{approved ? "律师实名认证已通过" : "认证审核中"}</h1>
          <p>{approved ? "身份与执业信息已通过平台审核。" : "平台正在审核您提交的认证资料。"}</p>
        </div>
      </header>
      <h2>认证信息</h2>
      <dl className="personal-lawyer-details">
        <div><dt>律师姓名</dt><dd>{profile.lawyer_name}</dd></div>
        <div><dt>所在律所</dt><dd>{profile.law_firm_name ?? "—"}</dd></div>
        <div><dt>身份证号</dt><dd>{profile.identity_number ?? "—"}</dd></div>
        <div><dt>律师执业证号</dt><dd>{profile.practice_certificate_number ?? "—"}</dd></div>
      </dl>
      <h2>认证材料</h2>
      <div className="personal-lawyer-materials">
        {profile.materials.map((material) => (
          <article key={material.id}>
            <strong>{MATERIAL_LABELS[material.kind]}</strong>
            <span>{material.name}</span>
            <a href={appPath(material.review_url)} target="_blank" rel="noreferrer">查看水印副本</a>
          </article>
        ))}
      </div>
    </section>
  );
}

export function IdentityVerification({
  profile,
  onSubmitted,
}: {
  profile: PersonalLawyerProfile;
  onSubmitted: (profile: PersonalLawyerProfile) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (profile.status === "PENDING" || profile.status === "APPROVED") {
    return <SubmittedVerification profile={profile} />;
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const identityFront = form.get("identity_front");
    const identityBack = form.get("identity_back");
    const practiceCertificate = form.get("practice_certificate");
    if (!(identityFront instanceof File) || !(identityBack instanceof File) || !(practiceCertificate instanceof File)) {
      setError("请完整上传三份认证材料。");
      return;
    }
    setBusy(true);
    setError("");
    try {
      onSubmitted(await submitPersonalLawyerVerification({
        lawyerName: String(form.get("lawyer_name") ?? ""),
        lawFirmName: String(form.get("law_firm_name") ?? ""),
        identityNumber: String(form.get("identity_number") ?? ""),
        practiceCertificateNumber: String(form.get("practice_certificate_number") ?? ""),
        identityFront,
        identityBack,
        practiceCertificate,
      }));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "提交失败，请重试。");
    } finally {
      setBusy(false);
    }
  }

  const rejected = profile.status === "REJECTED";
  return (
    <section className="personal-lawyer-panel personal-lawyer-verification">
      {rejected && <div className="personal-lawyer-rejection" role="alert"><strong>审核未通过</strong><span>{profile.rejection_reason}</span></div>}
      <form className="personal-lawyer-form" onSubmit={(event) => void submit(event)}>
        <label><span>律师姓名</span><input name="lawyer_name" defaultValue={profile.lawyer_name} required maxLength={64} /></label>
        <label><span>所在律所全称</span><input name="law_firm_name" defaultValue={profile.law_firm_name ?? ""} required maxLength={160} /></label>
        <label><span>本人身份证号</span><input name="identity_number" required maxLength={18} /></label>
        <label><span>律师执业证号</span><input name="practice_certificate_number" required maxLength={32} /></label>
        <label className="personal-lawyer-upload"><span>身份证人像面</span><input name="identity_front" type="file" accept="image/jpeg,image/png" required /><small>支持 JPEG、PNG，单张不超过 10MB</small></label>
        <label className="personal-lawyer-upload"><span>身份证国徽面</span><input name="identity_back" type="file" accept="image/jpeg,image/png" required /></label>
        <label className="personal-lawyer-upload"><span>律师执业证</span><input name="practice_certificate" type="file" accept="image/jpeg,image/png" required /></label>
        <p className="personal-lawyer-form-note">平台会生成带专用水印的审核副本，原图不对客户端开放。</p>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button type="submit" disabled={busy}>{busy ? "提交中…" : "提交实名认证"}</button>
      </form>
    </section>
  );
}
